import type { Building } from "building-types/building/buildingTypes";
import type { Doc } from "building-types/doc";
import type { DoorDescriptor } from "building-types/doors/doorTypes";
import type { LoadDescriptor } from "building-types/electrical/electricalTypes";
import { LoadType } from "building-types/electrical/electricalTypesEnum";
import type { SettingsHandle } from "building-types/handles/docEntityHandleTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import { RoomLabelDimensionType, RoomLabelRounding, RoomLabelUnits } from "building-types/rooms/roomTypesEnum";
import type { BaseSettings } from "building-types/settings/baseSettings";
import type { DoorSettings } from "building-types/settings/doorSettings";
import type { ElectricalItemCatalogSettings } from "building-types/settings/electricalItemCatalogSettings";
import type { ElectricalRule } from "building-types/settings/electricalRuleTypes";
import type { ExteriorDoorCasingSettings } from "building-types/settings/exteriorDoorCasingSettings";
import type { ExteriorWindowCasingSettings } from "building-types/settings/exteriorWindowCasingSettings";
import type {
  HeadCasingArchedSlice,
  HeadCasingArchedSlices,
  HeadCasingEyebrowTrim,
  HeadCasingFullArch,
  HeadCasingNone,
  HeadCasingSettings,
  HeadCasingSlice,
  HeadCasingSlices,
} from "building-types/settings/headCasingSettings";
import type { InteriorWindowCasingSettings } from "building-types/settings/interiorWindowCasingSettings";
import type { PlanSettings } from "building-types/settings/planSettings";
import type { Settings } from "building-types/settings/settings";
import type { SettingsFragment } from "building-types/settings/settingsFragment";
import type { SettingsSet } from "building-types/settings/settingsSet";
import { SettingsSetBehaviorType } from "building-types/settings/settingsSetBehaviorType";
import { BUILT_IN_SETTINGS_TYPE_PREFIX, builtInSettingTypes, SettingsType } from "building-types/settings/settingsType";
import { EaveType, HeadCasingType, WindowMullionType } from "building-types/settings/settingsTypesEnum";
import type { WindowMullionSettings } from "building-types/settings/windowMullionSettings";
import type { WindowSettings } from "building-types/settings/windowSettings";
import { WindowShapeTypes } from "building-types/settings/windowTypesEnum";
import type { WindowDescriptor } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { snakeCaseToTitleCase } from "common/utils/strings";
import { ineffectualSettingsFields } from "./ineffectualSettingsFields";
import { rectangleMullionTypes } from "./settingsTypes/rectangleMullionTypes";

export function roughOpeningWidthHeightCalcs(
  descriptor?: WindowDescriptor | DoorDescriptor,
  settings?: DoorSettings | WindowSettings,
) {
  if (!descriptor) {
    return {
      framingRoughOpeningWidth: null,
      framingRoughOpeningHeight: null,
      foundationRoughOpeningWidth: null,
      foundationRoughOpeningHeight: null,
    };
  }
  const windowFramingSill = (settings as WindowSettings)?.framingRoughOpeningSillDelta ?? 0;
  const windowFoundationSill = (settings as WindowSettings)?.foundationRoughOpeningSillDelta ?? 0;
  return {
    framingRoughOpeningWidth: (descriptor?.width ?? 0) + (settings?.framingRoughOpeningJambDelta ?? 0),
    framingRoughOpeningHeight:
      (descriptor?.height ?? 0) + (settings?.framingRoughOpeningHeadDelta ?? 0) + windowFramingSill,
    foundationRoughOpeningWidth: (descriptor?.width ?? 0) + (settings?.foundationRoughOpeningJambDelta ?? 0),
    foundationRoughOpeningHeight:
      (descriptor?.height ?? 0) + (settings?.foundationRoughOpeningHeadDelta ?? 0) + windowFoundationSill,
  };
}

export function headCasingNoneNew(): HeadCasingNone {
  return { headCasingType: HeadCasingType.NONE };
}

export function headCasingSlicesNew(
  slices: HeadCasingSlice[] = [
    { height: 6, depthOffset: 0.2, widthOffset: 0 },
    { height: 1, depthOffset: 0.5, widthOffset: 1 },
    { height: 1, depthOffset: 0.8, widthOffset: 1 },
  ],
  includeKeystone = true,
  keystoneHeight = 10,
  keystoneDepth = 2,
  keystoneSlope = 4,
  keystoneBottomWidth = 4,
): HeadCasingSlices {
  return {
    headCasingType: HeadCasingType.SLICES,
    headCasingSlices: slices,
    includeKeystone,
    keystoneHeight,
    keystoneDepth,
    keystoneSlope,
    keystoneBottomWidth,
  };
}

export function headCasingStoneNew(height: number = 6, depth: number = 1): HeadCasingSettings {
  return {
    headCasingType: HeadCasingType.STONE,
    headCasingHeight: height,
    headCasingDepth: depth,
  };
}

export function headCasingTrimNew(
  height: number = 3.5,
  depth: number = 0.75,
  extension: number = 0,
): HeadCasingSettings {
  return {
    headCasingType: HeadCasingType.TRIM,
    headCasingHeight: height,
    headCasingDepth: depth,
    headCasingExtension: extension,
  };
}

export function headCasingJackArchNew(
  height: number = 6,
  depth: number = 1,
  includeKeystone = true,
  keystoneHeight = 8,
  keystoneDepth = 2,
  keystoneSlope = 4,
  keystoneBottomWidth = 6,
): HeadCasingSettings {
  return {
    headCasingType: HeadCasingType.JACK_ARCH,
    headCasingHeight: height,
    headCasingDepth: depth,
    includeKeystone,
    keystoneHeight,
    keystoneDepth,
    keystoneSlope,
    keystoneBottomWidth,
  };
}

export function headCasingFullArchNew(
  headCasingHeight: number = 4,
  headCasingDepth: number = 1,
  material: ExteriorMaterial = "masonryTrim0:Rowlock",
  invertUV: boolean = false,
): HeadCasingFullArch {
  return {
    headCasingType: HeadCasingType.FULL_ARCH,
    headCasingHeight,
    headCasingDepth,
    material,
    invertUV,
  };
}

export function headCasingArchedSlicesNew(
  slices: HeadCasingArchedSlice[] = [{ height: 6, depthOffset: 2, material: "trimExterior0" }],
  headCasingArcHeight: number = 5,
  headCasingInteriorDepth: number = 0.5,
  headCasingInteriorMaterial: ExteriorMaterial = "headCasing0",
): HeadCasingArchedSlices {
  return {
    headCasingType: HeadCasingType.ARCHED_SLICES,
    headCasingArchedSlices: slices,
    headCasingArcHeight,
    headCasingInteriorDepth,
    headCasingInteriorMaterial,
  };
}

export function headCasingEyebrowTrimNew(
  headCasingHeight: number = 10,
  headCasingDepth: number = 1,
  headCasingExtension: number = 0,
  headCasingArcHeight: number = 5,
  material: ExteriorMaterial = "headCasing0",
): HeadCasingEyebrowTrim {
  return {
    headCasingType: HeadCasingType.EYEBROW_TRIM,
    headCasingHeight,
    headCasingDepth,
    headCasingExtension,
    headCasingArcHeight,
    material,
  };
}
export function supportedShapeMullionTypes(shape: WindowShapeTypes | undefined) {
  if (shape === WindowShapeTypes.RECTANGLE || shape === WindowShapeTypes.RECTANGLE_ARCHED_TOP) {
    return Object.keys(rectangleMullionTypes);
  } else {
    return Object.keys(WindowMullionType);
  }
}

export function isCustomMullionType(settings: WindowMullionSettings | undefined) {
  if (!settings) {
    return false;
  }
  return (
    settings.mullionType === WindowMullionType.ORTHOGONAL_CUSTOM ||
    settings.mullionType === WindowMullionType.RADIAL_CUSTOM
  );
}

export function getRoomLabelAttributes(
  building: Building,
): Pick<PlanSettings, "roomLabelRounding" | "roomLabelUnits" | "roomLabelDimensionType"> {
  return {
    roomLabelRounding: building?.settings?.planset?.roomLabelRounding ?? RoomLabelRounding.LOWEST,
    roomLabelUnits: building?.settings?.planset?.roomLabelUnits ?? RoomLabelUnits.WHOLE_FEET,
    roomLabelDimensionType:
      building?.settings?.planset?.roomLabelDimensionType ?? RoomLabelDimensionType.ROOM_DECOMPOSITION,
  };
}

export function isExteriorWindowCasingSettings(settings?: any): settings is ExteriorWindowCasingSettings {
  return !!settings && settings.type === SettingsType.EXTERIOR_WINDOW_CASING_SETTINGS;
}

export function isExteriorDoorCasingSettings(settings: any): settings is ExteriorDoorCasingSettings {
  if (!settings) {
    return false;
  }
  return settings.type === SettingsType.EXTERIOR_DOOR_CASING_SETTINGS;
}

export function isInteriorWindowCasingSettings(settings: any): settings is InteriorWindowCasingSettings {
  return settings?.type === SettingsType.INTERIOR_WINDOW_CASING_SETTINGS;
}

export function getUserAddedElectricalLoadTypeSrcHandle(
  loadType: string,
  loadCatalog: Table<LoadDescriptor>,
  catalog: ElectricalItemCatalogSettings,
): SettingsHandle | undefined {
  if (!LoadType[loadType] && loadCatalog[loadType] && catalog.id) {
    return { type: "SETTINGS", id: catalog.id };
  }
  return undefined;
}

export interface ElectricalFilterProps {
  readonly rule: ElectricalRule;
  readonly onChange: (rule: ElectricalRule) => void;
}

export function filterIneffectualSettingsFields(settings: SettingsFragment) {
  const filteredSettings: Partial<SettingsFragment> = { ...settings };

  for (const key of ineffectualSettingsFields) {
    delete filteredSettings[key];
  }

  return filteredSettings;
}

export const isVirtualLoad: {
  [K in LoadType]?: boolean;
} = {
  [LoadType.VIRTUAL_CEILING_FAN_LIGHT]: true,
};

export const supportsEaveCutType: {
  [key in EaveType]?: boolean;
} = {
  [EaveType.BOXED_FULL_RETURN]: true,
  [EaveType.BOXED_OPEN]: true,
  [EaveType.BOXED_MINI_HIP]: true,
  [EaveType.ANGLED_ENCLOSED_OPEN]: true,
  [EaveType.ANGLED_OPEN_OPEN]: true,
  [EaveType.BOXED_MINI_SHED]: true,
  [EaveType.BOXED_BOX]: true,
};

export const supportsEaveReturnDistance: {
  [key in EaveType]?: boolean;
} = {
  [EaveType.BOXED_BOX]: true,
  [EaveType.BOXED_MINI_HIP]: true,
  [EaveType.BOXED_MINI_SHED]: true,
};

export const supportsEaveReturnOverhang: {
  [key in EaveType]?: boolean;
} = {
  [EaveType.BOXED_MINI_HIP]: true,
  [EaveType.BOXED_MINI_SHED]: true,
  [EaveType.BOXED_FULL_RETURN]: true,
};

export const supportsAngledEaveReturn: {
  [key in EaveType]?: boolean;
} = {
  [EaveType.BOXED_MINI_HIP]: true,
  [EaveType.BOXED_MINI_SHED]: true,
  [EaveType.BOXED_FULL_RETURN]: true,
};

export function entityIsSetting(entity: any): entity is Settings {
  return entity?.["entityType"] && entity.entityType === "SETTINGS";
}

export function entityIsSettingsSet(entity: any): entity is SettingsSet {
  return entity?.["entityType"] && entity.entityType === "SETTINGS_SET";
}

export function entityIsGlobalSettingsSet(entity: any): boolean {
  return entityIsSettingsSet(entity) && entity.type === SettingsSetBehaviorType.GLOBAL;
}

export function entityIsTargetedSettingsSet(entity: any): boolean {
  return entityIsSettingsSet(entity) && entity.type === SettingsSetBehaviorType.TARGETED;
}

export function formatSettingsType(settingsType: SettingsType): string {
  if (isBuiltInSettings(settingsType)) {
    return `${BUILT_IN_SETTINGS_TYPE_PREFIX} ${snakeCaseToTitleCase(settingsType).replace(" Settings", "")}`;
  }
  return snakeCaseToTitleCase(settingsType).replace(" Settings", "");
}

export function isBuiltInSettings(settingsType: SettingsType): boolean {
  return builtInSettingTypes.includes(settingsType as any);
}

export function entityIsTargetedSettings(doc: Doc, settings: BaseSettings): boolean {
  if (!settings.parentId) {
    return false;
  }

  const { settingsSets } = doc;
  const settingsSet = settingsSets[settings.parentId];

  return settingsSet?.type === SettingsSetBehaviorType.TARGETED;
}

export function sortSettingsTypes(settingsTypes: SettingsType[]): SettingsType[] {
  const sortedSettingsTypes = settingsTypes.sort((a, b) => {
    const aType = formatSettingsType(a);
    const bType = formatSettingsType(b);
    return aType.localeCompare(bType);
  });

  return sortedSettingsTypes;
}

export function sortSettingsByFormattedType(settings: SettingsFragment[]): SettingsFragment[] {
  const sortedSettingsTypes = sortSettingsTypes(settings.map((s) => s.type));
  const settingsMap: { [id: string]: SettingsFragment } = {};

  for (const setting of settings) {
    settingsMap[setting.type] = setting;
  }

  return sortedSettingsTypes.map((type) => settingsMap[type]);
}
